import React, { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, XCircle, FileCheck2 } from "lucide-react";

export default function BatchPay() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [items, setItems] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const fileRef = useRef();

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  const projFilter = selectedProject && selectedProject !== "__ALL__" ? selectedProject : "";
  const load = async () => {
    const q = projFilter ? `&project_id=${projFilter}` : "";
    const r = await api.get(`/reimbursements?status=Approved${q}`);
    setItems(r.data.filter(x => x.payment_status !== "Processed"));
  };
  useEffect(() => { load(); }, [selectedProject]);

  const exportCsv = async () => {
    try {
      const q = projFilter ? `?project_id=${projFilter}` : "";
      const r = await api.get(`/payments/export-batch${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `batch-pay-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} row(s)`);
    } catch { toast.error("Export failed"); }
  };

  const importUtr = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/payments/import-utr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResults(r.data);
      toast.success(`Posted ${r.data.posted} UTR entries`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const totalPayable = items.reduce((s, i) => s + (i.payable_amount || 0), 0);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Money · Batch Payments</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Batch Pay File</h1>
        <p className="text-sm text-slate-500 mt-1">Export approved-unpaid to bank format → upload to bank portal → import UTR file back to close in bulk.</p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Project (or all)</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="batch-project-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={exportCsv} disabled={items.length === 0} className="bg-[#0F172A]" data-testid="export-batch-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV ({items.length})
          </Button>
          <div>
            <input type="file" ref={fileRef} accept=".csv" onChange={importUtr} className="hidden" />
            <Button onClick={() => fileRef.current?.click()} variant="outline" className="w-full" data-testid="import-utr-btn">
              <Upload className="w-4 h-4 mr-1" /> Import UTR CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-slate-500">Pending Payments</div><div className="font-display font-bold text-2xl mt-1">{items.length}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-slate-500">Total Payable</div><div className="font-display font-bold text-2xl mt-1 text-emerald-700">₹ {totalPayable.toLocaleString()}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-slate-500">Distinct Payees</div><div className="font-display font-bold text-2xl mt-1">{new Set(items.map(i => i.payee_id)).size}</div></CardContent></Card>
      </div>

      <Card className="mb-6">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="text-left px-3 py-2">RMB</th>
                <th className="text-left px-3 py-2">Payee</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-right px-3 py-2">Gross</th>
                <th className="text-right px-3 py-2">TDS</th>
                <th className="text-right px-3 py-2">Payable</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500">No pending payments.</td></tr>}
              {items.map(r => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`batch-row-${r.rmb_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.rmb_id}</td>
                  <td className="px-3 py-2 font-medium">{r.payee_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.invoice_number}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.expense_month}</td>
                  <td className="px-3 py-2 text-right font-mono">₹ {r.gross_amount?.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-700">− ₹ {r.tds_amount?.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">₹ {r.payable_amount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {importResults && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm flex items-center gap-2"><FileCheck2 className="w-4 h-4" /> Import Results</div>
              <div className="text-xs text-slate-500">{importResults.posted} of {importResults.results?.length || 0} rows posted</div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500"><tr className="border-b"><th className="text-left px-2 py-1.5">RMB</th><th className="text-left px-2 py-1.5">Status</th><th className="text-left px-2 py-1.5">Result</th></tr></thead>
                <tbody>
                  {importResults.results?.map((x, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-xs">{x.rmb_id}</td>
                      <td className="px-2 py-1.5">{x.payment_status || "—"}</td>
                      <td className="px-2 py-1.5">{x.status === "updated" ? <Badge className="bg-emerald-600 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />Updated</Badge> :
                                                     <Badge variant="destructive" className="text-[10px]"><XCircle className="w-3 h-3 mr-1" />{x.status}</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6 bg-slate-50 border-dashed">
        <CardContent className="p-4 text-xs text-slate-600">
          <div className="font-semibold mb-1">CSV Formats</div>
          <div><b>Export:</b> rmb_id, payee_name, bank, account_number, ifsc, payable_amount, invoice_number, expense_month, purpose</div>
          <div className="mt-1"><b>UTR Import:</b> rmb_id, utr, pay_date (YYYY-MM-DD), status (Processed|Failed|Returned), bank</div>
        </CardContent>
      </Card>
    </div>
  );
}
